import Client from "../Client";
import Exceptions from "../Exceptions";
import { getLGUList, getProvinceList } from "../services/api/meta";
import { AlertDrawerController } from "../views/sections/sidebar/AlertDrawer";
import { Dynamic, Notifier } from "./conductor";
import { showModal } from "../views/widgets/modal";
import { Button } from "../views/widgets/buttons";
import { addAccount, confirmAccount, deleteAccount, getAccountsList, updateAccount } from "../services/api/account";
import { getRoleLabel } from "../misc/account";
import { AccountFactory } from "../views/widgets/modals/account";
import { wait } from "../misc/date";
import { useState } from "react";
import { listToCollection } from "../misc/object";


export class AccountsController {
    constructor(options) {

        /** @type {{ lgu: string, id: string }} */
        this.reference = options.ref;

        const self = this;        
        this.notifiers = {
            ready: new Notifier(false),
            unauthorized: new Notifier(false),
            reloading: new Notifier(false),
            reloaded: new Notifier(),
            alert: new AlertDrawerController(),
            adding: new Notifier(false)
        };

        this.status = {
            get ready() { return self.notifiers.ready.get() },
            get reloading() { return self.notifiers.reloading.get() },
            get adding() { return self.notifiers.adding.get() },
            get unauthorized() { return self.notifiers.unauthorized.get() }
        };

        this.initialize();
    }

    async initialize() {
        Client.ensureSignedIn();
        if(this.isInitialized) return; this.isInitialized = true;

        try {
            this.collection = {
                province: listToCollection(await getProvinceList()),
                lgu: listToCollection(await getLGUList())
            };
            
            await this.loadData();
        } catch(e) {
            if(e.unauthorized) return this.notifiers.unauthorized.update(true);
        }
        
        this.notifiers.ready.update(true);
    }

    pageCache = {}

    async loadData() {
        const filters = this.reference.filters;
        if(filters.role === undefined) delete filters.role; 
        if(filters.confirmed === undefined) delete filters.confirmed;

        const cached = this.pageCache[filters.page];
        if(cached) this.data = {...cached};
        else {
            this.data = await getAccountsList(filters);
            // this.pageCache[filters.page] = {...this.data};
        }
        for(const account of this.data.results) {
            if(!("confirmed" in account)) account.confirmed = true;
            if(!account.createdAt) account.createdAt = "";
            if(!account.updatedAt) account.updatedAt = "";
            account.role_semantic = getRoleLabel(account.role, account.role_specific, this.collection);
        }
        // const { province, month, year } = this.reference;
        // this.data = await getProvinceVisitsSubmissions({ province, month, year });
    }

    async refresh(closeAlert = true) {
        const { reloading, reloaded, alert } = this.notifiers;
        reloading.update(true);
        if(closeAlert) alert.close();

        try {
            await this.loadData();
            reloaded.notify();
        } catch(e) {
            const semantics = Exceptions.getARESemantics(e);
            alert.alert(semantics.message, { status: "error", title: semantics.title, retry: () => this.refresh() });
        }

        reloading.update(false);
    }
    
    async changeLocation(province, lgu) {
        this.reference.province = province;
        this.reference.lgu = lgu;
        await this.refresh();

        // MainRouter.pushPath(`/forms/sameday/province/${this.reference.province}/submissions/${month}/${year}`);
    }

    getProvinces() { return Object.values(this.collection.province) };
    getLGUs(province) {
        const lgus = Object.values(this.collection.lgu)
            .filter(lgu => lgu.province === province);

        return lgus;
    };


    async addAccount() {
        const { notifiers: { alert } } = this;
        const action = async (account) => {
            try {
                await addAccount({ ...account });
                return { ok: true };
            } catch(e) {
                console.log(e);
                const { title, message } = Exceptions.getARESemantics(e);
                return { ok: false, error: message };
            }
        };
        
        const added = await this.addOrEditAccount(null, action);

        if(added) {
            alert.alert("A new account has been added successfully.");
            this.refresh(false);
        }
    };

    async editAccount(account) {
        const { notifiers: { alert } } = this;
        const action = async (account) => {
            try {
                await updateAccount({ ...account });
                return { ok: true };
            } catch(e) {
                console.log(e);
                const { title, message } = Exceptions.getARESemantics(e);
                return { ok: false, error: message };
            }
        };
        
        const updated = await this.addOrEditAccount(account, action);

        if(updated) {
            alert.alert(`Account '${account.fullname}' has been updated successfully.`);
            this.refresh(false);
        }
    };


    addOrEditAccount(account, action) {
        return new Promise(async resolve => {
            const modifying = account ? true : false;

            const controller = {};
            const saving = new Notifier(false);

            const onChange = a => {
                account = { ...account, ...a };
            };
            
            const save = async () => {
                const error = (error) => controller.setError(error);
                error(null);

                // Pre-Validation
                if(!account.fullname.trim()) return error("Fullname is required");
                if(!account.username.trim()) return error("Username is required");
                if(!modifying && !account.password.trim()) return error("Password is required");
    
                if(account.password.trim() && account.password !== account.confirmPassword) 
                    return error("Password must match Confirmation Password")

                controller.setSaving(true);
                saving.update(true);

                if(!account.password.trim()) delete account.password;

                const result = await action(account);
                if(result.ok) {
                    controller.close();
                    resolve(true);
                } else {
                    controller.setError(result.error);
                    saving.update(false);
                }

            };
        
            await showModal({
                title: modifying ? "Edit Account" : "Add Account",
                width: 400,
                body: <AccountFactory account={account} collection={this.collection} onChange={onChange} controller={controller} />,
                footer: <div style={{ textAlign: "right" }}>
                    <Dynamic conductor={saving} child={()=>
                        <Button icon="save" color="green" onClick={save} disabled={saving.get()}>{saving.get() ? "SAVING..." : "SAVE"}</Button> }/>
                </div>
            }, controller);

            resolve();
        });
    };

    async deleteAccount(account) {
        const { notifiers: { alert } } = this;

        const returned = await showModal({ 
            title: "Verify Deletion",
            titleColor: "red",
            body: <span>
                You are about to <b>delete</b> the account for <b>{account.fullname}</b> <span className="subtext-2">({account.username})</span>. 
                Please confirm this action by  
                <b> typing in the password</b> to this account.

                <br /><br />
                <i>NOTE: Documents created by this account will still refer to the name of this account.</i>
            </span>, footer: [],
            withPassword: true
        });

        if(!returned) return;
        const password = returned.password;

        alert.alert("Deleting account...", { status: "progress" });

        try {
            await deleteAccount({ id: account.id, password });
            alert.alert("Account deleted successfully!");
        } catch(e) {
            console.log(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.deleteAccount(account) });
        }

        this.refresh(false);
    }

    async confirmAccount(account) {
        const { notifiers: { alert } } = this;
        const { title, specific } = getRoleLabel(account.role, account.role_specific, this.collection);

        const returned = await showModal({ 
            title: "Verify Confirmation",
            titleColor: "green",
            body: <span>
                You are about to <b>confirm</b> the account for <b>{account.fullname}</b> <span className="subtext-2">({account.username})</span> with the role <b>{title}</b> 
                { specific ? <i> ({specific})</i> :  ""}.<br/><br/>
                
                Please confirm this action by  
                <b> typing in the password</b> to this account.

            </span>, footer: [],
            withPassword: true
        });

        if(!returned) return;
        const password = returned.password;

        alert.alert("Confirming account...", { status: "progress" });

        try {
            await confirmAccount({ id: account.id, password });
            alert.alert("Account confirmed successfully!");
            this.refresh(false);
        } catch(e) {
            console.log(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.confirmAccount(account) });
        }

    }

    async search(filters) {
        this.reference.filters = { ...this.reference.filters, ...filters };
        this.pageCache = {};

        this.refresh();
    }

    async changePage(page) {
        console.log("Navigating to page", page);
        this.reference.filters.page = page;
        this.refresh();
    }
}